import PatientAPI from 'api/PatientAPI'
import UploadsAPI from 'api/UploadsAPI'
import axios from 'axios'
import { buildIntakePdfFile, buildIntakeSummaryText, buildIntakeUploadFileName } from '~/helpers/intakePdf'

const INTAKE_DOCUMENT_RECORD_TYPE_ID = 15
const INTAKE_DOCUMENT_SOURCE_NAME = 'Intake Form'
const FALLBACK_INTAKE_PDF_DATA_URI =
    'data:application/pdf;base64,JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0NvdW50IDEvS2lkc1szIDAgUl0+PgplbmRvYmoKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDIgMCBSL01lZGlhQm94WzAgMCA2MTIgNzkyXS9Db250ZW50cyA0IDAgUj4+CmVuZG9iago0IDAgb2JqCjw8L0xlbmd0aCA0ND4+CnN0cmVhbQpCVAovRjEgMjQgVGYKMTAwIDcwMCBUZAooSW50YWtlIEZvcm0pIFRqCkVUCmVuZHN0cmVhbQplbmRvYmoKeHJlZgowIDUKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMDE1IDAwMDAwIG4gCjAwMDAwMDAwNjQgMDAwMDAgbiAKMDAwMDAwMDEyMSAwMDAwMCBuIAowMDAwMDAwMjA5IDAwMDAwIG4gCnRyYWlsZXIKPDwvU2l6ZSA1L1Jvb3QgMSAwIFI+PgpzdGFydHhyZWYKMzA0CiUlRU9GCg=='

function fileFromDataUri(dataUri, fileName, mimeType = 'application/pdf') {
    const base64 = String(dataUri).split(',')[1] || ''
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index)
    }

    return new File([bytes], fileName, { type: mimeType })
}

function fallbackIntakePdfFile(payload) {
    const fileName = buildIntakeUploadFileName(payload || {})
    const uploadFile = fileFromDataUri(FALLBACK_INTAKE_PDF_DATA_URI, fileName, 'application/pdf')

    return {
        fileName,
        file: FALLBACK_INTAKE_PDF_DATA_URI,
        uploadFile,
        size: Math.max(1, Math.round(uploadFile.size / 1024)),
    }
}

function toUploadFile(pdfFile) {
    if (pdfFile.uploadFile instanceof File) {
        return pdfFile.uploadFile
    }

    return fileFromDataUri(pdfFile.file, pdfFile.fileName || 'IntakeForm.pdf', 'application/pdf')
}

export default {
    /**
     * Submit signed intake by updating the patient record.
     * PUT /patient/:patientId/user/:userId
     */
    submitForPatient(patientId, userId, payload) {
        return axios.put(`/patient/${Number(patientId)}/user/${Number(userId)}`, payload)
    },

    /**
     * Upload generated Intake Form.pdf via UploadsAPI (presign → S3), then documents/upload with uploadKey.
     */
    async uploadIntakeDocument({ patientId, fromPracticeId, pdfFile }) {
        const uploadFile = toUploadFile(pdfFile)
        const [uploaded] = await UploadsAPI.uploadFiles([uploadFile], 'patient-documents')

        return PatientAPI.uploadDocuments({
            patientId: Number(patientId),
            fromPracticeId: Number(fromPracticeId),
            recordTypeId: INTAKE_DOCUMENT_RECORD_TYPE_ID,
            sourceName: INTAKE_DOCUMENT_SOURCE_NAME,
            date: new Date().toISOString().slice(0, 10),
            files: [
                {
                    fileName: uploaded.fileName,
                    size: Math.max(1, uploaded.size),
                    uploadKey: uploaded.uploadKey,
                },
            ],
        })
    },

    /**
     * Full signed-intake flow: save patient intake, then upload the PDF record.
     * Returns { summaryText, pdfFile } for the success screen.
     */
    async submitAndUpload({ patientId, userId, fromPracticeId, payload }) {
        await this.submitForPatient(patientId, userId, payload)

        let pdfFile
        try {
            pdfFile = buildIntakePdfFile(payload || {})
        } catch (error) {
            pdfFile = fallbackIntakePdfFile(payload)
        }

        await this.uploadIntakeDocument({
            patientId,
            fromPracticeId,
            pdfFile,
        })

        return {
            summaryText: buildIntakeSummaryText(payload || {}),
            pdfFile,
        }
    },
}
